import React, { useState } from "react";
import axios from "axios";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import * as enums from '../../../../../utils/EnumMapping';
import { fmt } from '../../../../../utils/Util';
// adjust import paths for your ErrorAware components if needed
import ErrorAwareDatePicker from '../../../../common/inputfields/ErrorAwareDatePicker';
import ErrorAwareTextField from '../../../../common/inputfields/ErrorAwareTextField';
import { Divider } from "@material-ui/core";

export default function ProjectPhaseHistory({ history = [], nextTransitions = [], phasePrivateId = null }) {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuItems, setMenuItems] = useState([]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogEvent, setDialogEvent] = useState(null);
    const [dialogDate, setDialogDate] = useState(null);
    const [dialogComment, setDialogComment] = useState("");

    const sorted = Array.isArray(history)
        ? [...history].sort((a, b) => (a.stateNumber || 0) - (b.stateNumber || 0))
        : [];

    const enriched = sorted.map((item, idx) => {
        const copy = {
            ...item,
            stateLabel: enums.getLabel(enums.projectPhaseStates, item.state) || item.state,
            eventLabel: item.event ? (enums.getLabel(enums.projectPhaseEvents, item.event) || item.event) : undefined,
            dateFormatted: fmt(item.date || item.startDate || item.createdAt)
        };
        return copy;
    });

    const enrichedNextTransitions = Array.isArray(nextTransitions) ? nextTransitions.map(t => ({
        eventKey: t.eventKey,
        eventLabel: enums.getLabel(enums.projectPhaseEvents, t.eventKey) || t.eventKey,
        nextStateKey: t.nextStateKey,
        nextStateLabel: enums.getLabel(enums.projectPhaseStates, t.nextStateKey) || t.nextStateKey
    })) : [];

    const revertEvent = {
        eventKey: "REVERT",
        eventLabel: "Visszaállítás az előző állapotra",
        nextStateKey: enriched[enriched.length - 2]?.state,
        nextStateLabel: enriched[enriched.length - 2]?.stateLabel
    }

    const lastIndex = enriched.length - 1;

    const openMenuFor = (e, items = []) => {
        setMenuAnchor(e.currentTarget);
        setMenuItems(items);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuItems([]);
    };

    const openDialogFor = (action) => {
        setDialogEvent(action);
        setDialogDate(null);
        setDialogComment("");
        setDialogOpen(true);
        closeMenu();
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setDialogEvent(null);
        setDialogDate(null);
        setDialogComment("");
    };

    const handleApply = async () => {
        console.log(dialogEvent);
        if (!dialogEvent) return;
        try {
            if (dialogEvent.eventKey === "REVERT") {
                const payload = {
                    privateId: phasePrivateId
                }
                console.log(payload);
                await axios.post("/api/revertprojectphase", payload);
            } else {
                const payload = {
                    privateId: phasePrivateId,
                    projectPhaseEventEnum: dialogEvent.eventKey,
                    date: dialogDate ? (typeof dialogDate === "string" ? dialogDate : dialogDate.toISOString().slice(0, 10)) : null,
                    comment: dialogComment || null
                };
                console.log(payload);
                await axios.post("/api/nextprojectphase", payload);
            }

            // notify app to refresh project detail and reopen the same phase accordion
            window.dispatchEvent(new CustomEvent("projectphase:nextApplied", { detail: { privateId: phasePrivateId } }));
        } catch (err) {
            // handle error (could add toast)
            console.error("Failed to apply next project phase", err);
        } finally {
            closeDialog();
        }
    };

    return (
        <div className="project-phase-history">
            <div className="phases-row" role="list">
                {enriched.map((item, idx) => {
                    const next = enriched[idx + 1];
                    const isCurrent = idx === lastIndex;
                    return (
                        <React.Fragment key={item.privateId || idx}>
                            <div
                                className={`phase-box ${isCurrent ? "current" : ""}`}
                                role="listitem"
                                aria-label={item.state || item.stateLabel}
                            >
                                <div className="phase-value">{item.stateLabel || item.state || "—"}</div>
                                {item.name ? <div className="phase-name">{item.name}</div> : null}
                            </div>

                            {next ? (
                                <div className="phase-connector" aria-hidden="true">
                                    <div className="arrow" aria-hidden>
                                        <svg width="28" height="12" viewBox="0 0 28 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                            <path d="M0 6h22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M18 1l6 5-6 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>

                                    <div className="arrow-tooltip" role="tooltip">
                                        <div className="tt-date">{fmt(next.date || next.startDate)}</div>
                                        <div className="tt-comment">{next.comment || "—"}</div>
                                    </div>
                                </div>
                            ) : null}
                        </React.Fragment>
                    );
                })}

                {enrichedNextTransitions.length > 0 && (
                    <>
                        {enriched.length > 0 ? (
                            <div className="phase-connector" aria-hidden="false">
                                <IconButton
                                    size="small"
                                    onClick={(e) => openMenuFor(e, enrichedNextTransitions)}
                                    aria-label="Lehetséges műveletek"
                                >
                                    <MoreHorizIcon />
                                </IconButton>
                            </div>
                        ) : (
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => openMenuFor(e, enrichedNextTransitions)}
                                    aria-label="Lehetséges műveletek"
                                >
                                    <MoreHorizIcon />
                                </IconButton>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Menu
                anchorEl={menuAnchor}
                keepMounted
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                disableScrollLock
            >
                {[
                    ...menuItems.map((mi, i) => (
                        <MenuItem
                            key={`${mi.eventKey}-${i}`}
                            onClick={() => openDialogFor(mi)}
                        >
                            {mi.eventLabel || mi.eventKey}
                        </MenuItem>
                    )),
                    ...(enriched.length > 1 ? [
                        <Divider key={`divider`} />,
                        <MenuItem
                            key={`${revertEvent.eventKey}-${0}`}
                            onClick={() => openDialogFor(revertEvent)}
                        >
                            {revertEvent.eventLabel || revertEvent.eventKey}
                        </MenuItem>
                    ] : [])
                ]}
            </Menu>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm" >
                <DialogTitle>{dialogEvent ? dialogEvent.eventLabel : "Művelet"}</DialogTitle>
                <DialogContent>
                    {dialogEvent?.eventKey === "REVERT" ? (
                        <>
                            <div>Biztos vagy benne hogy visszaállsz az előző státuszra? </div>
                            <div>Az ütem státusza vissza fog állni erre: '{dialogEvent.nextStateLabel}'</div>
                        </>
                    ) : (
                        <div style={{ display: "grid", gap: 12 }}>
                            <ErrorAwareDatePicker
                                label="Dátum"
                                value={dialogDate}
                                onChange={(v) => setDialogDate(v)}
                                required={true}
                            />
                            <ErrorAwareTextField
                                label="Megjegyzés"
                                value={dialogComment}
                                onChange={(e) => setDialogComment(e.target.value)}
                                multiline
                                rows={3}
                            />
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Mégse</Button>
                    <Button color="primary" variant="contained" onClick={handleApply}>Alkalmaz</Button>
                </DialogActions>
            </Dialog>
        </div >
    );
}